/**
 * Axios 请求封装（业务端专用）
 * @author LWX
 * @date 2026-03-23
 * @修改 2026-09-11 请求拦截器只取业务端 token，避免被客户端 token 污染
 */

import axios from 'axios'
import { Message } from 'element-ui'
import router from '@/router'
import store from '@/store'

// ========== 白名单配置 ==========
// 不需要 token 的公开接口
const WHITE_LIST = [
    '/api/captcha',
    '/api/login',
    '/api/register',
    '/api/forgot-password',
    '/api/refresh'
]

const service = axios.create({
    baseURL: process.env.VUE_APP_BASE_API || '/api',
    timeout: 60000
})

// 是否正在刷新token的标志，防止多个请求同时刷新
let isRefreshing = false
// 等待队列，存放刷新期间的其他请求
let requests = []
// 防止重复退出登录
let isLoggingOut = false

// ========== 请求拦截器 ==========
service.interceptors.request.use(
    config => {
        // 白名单接口：不添加 token
        const isWhiteList = WHITE_LIST.some(url => config.url.includes(url))
        if (isWhiteList) {
            delete config.headers['Authorization']
            if (config.url.includes('/api/captcha')) {
                config.params = {
                    ...config.params,
                    _t: Date.now()
                }
            }
            return config
        }

        // ===== 只取业务端 token，不看客户端 token =====
        let token = store.getters.token
        if (!token) {
            token = localStorage.getItem('ShareWin-Token')
        }

        if (token) {
            config.headers['Authorization'] = 'Bearer ' + token
        }

        // GET 请求添加时间戳防止缓存
        if (config.method?.toLowerCase() === 'get') {
            config.params = {
                ...config.params,
                _t: Date.now()
            }
        }

        return config
    },
    error => Promise.reject(error)
)

// ========== 响应拦截器 ==========
service.interceptors.response.use(
    response => {
        // 🔴 核心修复：如果是 blob 类型，直接返回 response.data，不要返回整个 response
        if (response.request.responseType === 'blob' || response.config.responseType === 'blob') {
            return response.data
        }

        const res = response.data

        if (response.config.silent) {
            return res
        }

        if (res.code !== 200) {
            // 401 处理
            if (res.code === 401) {
                if (isLoggingOut) {
                    return Promise.reject(new Error('正在退出登录...'))
                }

                isLoggingOut = true

                localStorage.removeItem('ShareWin-Token')
                localStorage.removeItem('ShareWin-Token-Time')
                localStorage.removeItem('ShareWin-User')
                localStorage.removeItem('ShareWin-Menus')
                localStorage.removeItem('ShareWin-Permissions')

                store.dispatch('user/logout').then(() => {
                    setTimeout(() => {
                        isLoggingOut = false
                        router.push('/login')
                        Message({ message: '您的账号已在其他设备登录，请重新登录', type: 'warning', duration: 3000 })
                    }, 100)
                })

                return Promise.reject(new Error('登录已过期，请重新登录'))
            }

            return Promise.reject(new Error(res.msg || '请求失败'))
        }
        return res
    },
    async error => {
        if (error.config && error.config.responseType === 'blob') {
            Message.error('导出文件失败，请检查数据或稍后重试')
            return Promise.reject(error)
        }

        const originalRequest = error.config

        // 白名单接口 401 特殊处理
        const isWhiteList = WHITE_LIST.some(url => originalRequest?.url?.includes(url))
        if (isWhiteList && error.response?.status === 401) {
            localStorage.removeItem('ShareWin-Token')
            delete originalRequest.headers['Authorization']
            return service(originalRequest)
        }

        // 401 自动刷新 token
        if (error.response?.status === 401 && !originalRequest._retry) {
            if (isLoggingOut) {
                return Promise.reject(error)
            }

            if (isRefreshing) {
                return new Promise((resolve) => {
                    requests.push(() => {
                        resolve(service(originalRequest))
                    })
                })
            }

            originalRequest._retry = true
            isRefreshing = true

            try {
                let userId = store.getters.user?.id

                if (!userId) {
                    const userStr = localStorage.getItem('ShareWin-User')
                    if (userStr) {
                        try {
                            const user = JSON.parse(userStr)
                            userId = user.id
                        } catch (e) {
                            console.error('解析用户信息失败', e)
                        }
                    }
                }

                if (!userId) {
                    if (!isLoggingOut) {
                        isLoggingOut = true
                        localStorage.removeItem('ShareWin-Token')
                        localStorage.removeItem('ShareWin-Token-Time')
                        localStorage.removeItem('ShareWin-User')
                        localStorage.removeItem('ShareWin-Menus')
                        localStorage.removeItem('ShareWin-Permissions')
                        store.dispatch('user/logout')
                        setTimeout(() => {
                            isLoggingOut = false
                            router.push('/login')
                        }, 100)
                    }
                    return Promise.reject(error)
                }

                const res = await axios.post('/api/refresh', {
                    userId: userId
                }, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                })

                if (res.data.code === 200) {
                    const newToken = res.data.data.token

                    localStorage.setItem('ShareWin-Token', newToken)
                    localStorage.setItem('ShareWin-Token-Time', Date.now().toString())
                    store.commit('user/SET_TOKEN', newToken)

                    requests.forEach(cb => cb())
                    requests = []

                    originalRequest.headers['Authorization'] = 'Bearer ' + newToken
                    return service(originalRequest)
                } else {
                    if (!isLoggingOut) {
                        isLoggingOut = true
                        localStorage.removeItem('ShareWin-Token')
                        localStorage.removeItem('ShareWin-Token-Time')
                        localStorage.removeItem('ShareWin-User')
                        localStorage.removeItem('ShareWin-Menus')
                        localStorage.removeItem('ShareWin-Permissions')
                        store.dispatch('user/logout')
                        setTimeout(() => {
                            isLoggingOut = false
                            router.push('/login')
                        }, 100)
                    }
                    return Promise.reject(error)
                }
            } catch (refreshError) {
                console.error('刷新token失败:', refreshError)
                if (!isLoggingOut) {
                    isLoggingOut = true
                    localStorage.removeItem('ShareWin-Token')
                    localStorage.removeItem('ShareWin-Token-Time')
                    localStorage.removeItem('ShareWin-User')
                    localStorage.removeItem('ShareWin-Menus')
                    localStorage.removeItem('ShareWin-Permissions')
                    store.dispatch('user/logout')
                    setTimeout(() => {
                        isLoggingOut = false
                        router.push('/login')
                    }, 100)
                }
                return Promise.reject(error)
            } finally {
                isRefreshing = false
            }
        }

        Message({ message: error.message || '网络错误', type: 'error', duration: 3000 })
        return Promise.reject(error)
    }
)

export default service
